import React, { useContext } from "react";
import {
  Box,
  Stack,
  TextField,
  MenuItem,
  Button,
  Alert,
  Chip,
  Typography,
} from "@mui/material";
import { useMutation } from "@apollo/client";

import {
  CREATE_JOB_POSTING,
  UPDATE_JOB_POSTING,
  RENEW_JOB_POSTING,
} from "../../../../platform/graphql/mutations/careJobs";

import LocationSection, {
  DEFAULT_LOCATION_RULE,
  type LocationRule,
  buildLocationSummary,
} from "./locationSection";

import PayInvoiceDialog from "../../../referralCareDirectory/careReferral/components/payInvoiceDialog";
import { UserContext } from "../../../../platform/components/auth/auth-context";
import { getEffectivePlan } from "../../../../platform/util/utils";

/* =========================================================
   Pricing & Plan Rules
========================================================= */
const JOB_POST_PRICE_USD = 49;
const RENEW_JOB_PRICE_USD = 29;
const STANDARD_PLAN_REQUIRED = 2;
const NON_STANDARD_ACTIVE_DAYS = 30;
const MS_IN_DAY = 24 * 60 * 60 * 1000;

/* =========================================================
   Types
========================================================= */
type MiniInvoice = { id: string; amount: number };

type EmploymentType = "FULL_TIME" | "PART_TIME" | "PER_DIEM" | "CONTRACT";
type PayUnit = "HOUR" | "DAY" | "MONTH" | "YEAR";

type JobDraft = {
  id?: number | string;
  title?: string;
  description?: string;
  employmentType?: EmploymentType;
  schedule?: string | null;
  location?: string | null;
  payMin?: number | null;
  payMax?: number | null;
  payUnit?: PayUnit;
  isActive?: boolean;
  expiresAt?: string | null;
  createdAt?: string | null;
  publishedAt?: string | null;
};

type Props = {
  businessId: number;
  initialValues?: JobDraft | null;
  initialJob?: JobDraft | null;
  onSaved?: () => void;
};

/* =========================================================
   Date helpers
========================================================= */
const isExpiredUtc = (iso?: string | null) =>
  !!iso && new Date(iso).getTime() < Date.now();

const daysUntil = (iso?: string | null) => {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

const toEpoch = (iso?: string | null) => {
  if (!iso) return null;
  const ts = Date.parse(iso);
  return Number.isFinite(ts) ? ts : null;
};

const nonStandardHardExpiryFrom = (job: JobDraft) => {
  const baseEpoch =
    toEpoch(job.publishedAt) ?? toEpoch(job.createdAt) ?? toEpoch(job.expiresAt);
  if (baseEpoch === null) return null;
  return new Date(baseEpoch + NON_STANDARD_ACTIVE_DAYS * MS_IN_DAY).toISOString();
};

const fmtDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString() : "-";

/* =========================================================
   Component
========================================================= */
export default function JobPostForm(props: Props) {
  const { businessId, onSaved } = props;
  const { providerUser } = useContext(UserContext);

  /* ---------------- Plan logic ---------------- */
  const effectivePlan = getEffectivePlan(providerUser);

  const hasStandardOrAbove = effectivePlan >= STANDARD_PLAN_REQUIRED;

  // Any user below Standard can post via one-time payment.
  const requiresPayment = !hasStandardOrAbove;

  /* ---------------- Initial job ---------------- */
  const init = (props.initialValues ?? props.initialJob ?? {}) as JobDraft;
  const isEdit = Boolean(init.id);
  const nonStandardExpiryIso =
    requiresPayment && isEdit ? nonStandardHardExpiryFrom(init) : null;
  const effectiveExpiryIso = nonStandardExpiryIso || init.expiresAt || null;

  /* ---------------- Local state ---------------- */
  const [title, setTitle] = React.useState(init.title ?? "");
  const [description, setDescription] = React.useState(init.description ?? "");
  const [employmentType, setEmploymentType] =
    React.useState<EmploymentType>(
      (init.employmentType as EmploymentType) ?? "FULL_TIME"
    );
  const [schedule, setSchedule] = React.useState(init.schedule ?? "");

  const [payMin, setPayMin] = React.useState<number | "">(
    typeof init.payMin === "number" ? init.payMin : ""
  );
  const [payMax, setPayMax] = React.useState<number | "">(
    typeof init.payMax === "number" ? init.payMax : ""
  );
  const [payUnit, setPayUnit] = React.useState<PayUnit>(
    (init.payUnit as PayUnit) ?? "HOUR"
  );

  const [isActive, setIsActive] = React.useState(init.isActive ?? true);
  const [error, setError] = React.useState<string | null>(null);

  /* ---------------- Expiry logic ---------------- */
  const expired = isExpiredUtc(effectiveExpiryIso);
  const daysLeft = daysUntil(effectiveExpiryIso);
  const nearExpiry = !expired && typeof daysLeft === "number" && daysLeft <= 5;

  /* ---------------- Location ---------------- */
  const initialLocationRule: LocationRule = React.useMemo(() => {
    const base = { ...DEFAULT_LOCATION_RULE };
    if (init.location?.trim()) base.locations = [init.location.trim()];
    return base;
  }, [init.location]);

  const [locationRule, setLocationRule] =
    React.useState<LocationRule>(initialLocationRule);

  const locationSummary = React.useMemo(
    () => buildLocationSummary(locationRule),
    [locationRule]
  );

  /* ---------------- GraphQL ---------------- */
  const [createJob, { loading: creating }] = useMutation(CREATE_JOB_POSTING);
  const [updateJob, { loading: updating }] = useMutation(UPDATE_JOB_POSTING);
  const [renewJob, { loading: renewing }] = useMutation(RENEW_JOB_POSTING);

  const loading = creating || updating || renewing;

  /* ---------------- Payment dialogs ---------------- */
  const [payOpen, setPayOpen] = React.useState(false);
  const [invoice, setInvoice] = React.useState<MiniInvoice | null>(null);

  const [renewOpen, setRenewOpen] = React.useState(false);
  const [renewInvoice, setRenewInvoice] =
    React.useState<MiniInvoice | null>(null);

  /* =========================================================
     CREATE (after payment OR free)
  ========================================================= */
  const createAfterPaid = async (_paypalCaptureId?: string) => {
    try {
      const { data } = await createJob({
        variables: {
          input: {
            businessId,
            title: title.trim(),
            description: description.trim(),
            employmentType,
            schedule: schedule.trim() || null,
            location: locationSummary.trim() || null,
            payMin: payMin === "" ? null : Number(payMin),
            payMax: payMax === "" ? null : Number(payMax),
            payUnit,
            isActive: true,
          },
        },
      });

      if (!data?.createJobPosting?.success) {
        throw new Error(data?.createJobPosting?.message || "Create failed");
      }

      onSaved?.();
    } catch (e: any) {
      setError(e.message || "Network error");
    } finally {
      setPayOpen(false);
      setInvoice(null);
    }
  };

  const renewAfterPaid = async (paypalCaptureId: string) => {
    if (!isEdit || !init.id) return;
    setError(null);
    try {
      const { data } = await renewJob({
        variables: {
          id: String(init.id),
          paypalCaptureId,
        },
      });

      if (!data?.renewJobPosting?.success) {
        throw new Error(data?.renewJobPosting?.message || "Renew failed");
      }

      onSaved?.();
    } catch (e: any) {
      setError(e.message || "Network error");
    } finally {
      setRenewOpen(false);
      setRenewInvoice(null);
    }
  };

  /* =========================================================
     SUBMIT HANDLER
  ========================================================= */
  const handleSubmit = async () => {
    setError(null);

    if (!title.trim()) return setError("Title is required.");
    if (!description.trim()) return setError("Description is required.");

    /* -------- EDIT -------- */
    if (isEdit) {
      if (expired) {
        setError(
          requiresPayment
            ? `This paid job has reached the ${NON_STANDARD_ACTIVE_DAYS}-day limit. Please renew to edit.`
            : "This job has expired. Please renew to edit."
        );
        return;
      }

      try {
        const { data } = await updateJob({
          variables: {
            id: String(init.id),
            patch: {
              title: title.trim(),
              description: description.trim(),
              employmentType,
              schedule: schedule.trim() || null,
              location: locationSummary.trim() || null,
              payMin: payMin === "" ? null : Number(payMin),
              payMax: payMax === "" ? null : Number(payMax),
              payUnit,
              isActive,
            },
          },
        });

        if (!data?.updateJobPosting?.success) {
          throw new Error(data?.updateJobPosting?.message || "Update failed");
        }

        onSaved?.();
      } catch (e: any) {
        setError(e.message || "Network error");
      }
      return;
    }

    /* -------- CREATE -------- */
    if (requiresPayment) {
      setInvoice({
        id: `JOBPOST-${businessId}-${Date.now()}`,
        amount: JOB_POST_PRICE_USD,
      });
      setPayOpen(true);
      return;
    }

    await createAfterPaid();
  };

  /* =========================================================
     RENEW
  ========================================================= */
  const openRenew = () => {
    if (!isEdit || !init.id) return;

    setRenewInvoice({
      id: `JOBRENEW-${init.id}-${Date.now()}`,
      amount: RENEW_JOB_PRICE_USD,
    });
    setRenewOpen(true);
  };

  /* =========================================================
     UI
  ========================================================= */
  return (
    <Box sx={{ p: 0.5 }}>
      {/* Status */}
      {isEdit && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          {expired ? (
            <>
              <Chip color="warning" label="Expired" size="small" />
              <Typography variant="body2">
                Expired on <b>{fmtDate(effectiveExpiryIso)}</b>
              </Typography>
              <Button
                size="small"
                variant="contained"
                onClick={openRenew}
                sx={{ ml: "auto" }}
              >
                Renew - ${RENEW_JOB_PRICE_USD}
              </Button>
            </>
          ) : nearExpiry ? (
            <>
              <Chip color="warning" label="Expiring soon" size="small" />
              <Typography variant="body2">
                Expires in {daysLeft} days
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={openRenew}
                sx={{ ml: "auto" }}
              >
                Renew early - ${RENEW_JOB_PRICE_USD}
              </Button>
            </>
          ) : null}
        </Stack>
      )}

      {!hasStandardOrAbove && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Job posting is <b>FREE</b> on the Standard plan and above.
          {" You can also post a single job for $49."}
        </Alert>
      )}

      {requiresPayment && isEdit && effectiveExpiryIso && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Paid job posts expire after {NON_STANDARD_ACTIVE_DAYS} days. Current
          expiry: <b>{fmtDate(effectiveExpiryIso)}</b>.
        </Alert>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      <Stack spacing={2}>
        <TextField label="Title *" value={title} onChange={(e) => setTitle(e.target.value)} />
        <TextField
          label="Description *"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          minRows={6}
        />

        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {requiresPayment
            ? `Post Job - $${JOB_POST_PRICE_USD}`
            : "Post Job (Free)"}
        </Button>
      </Stack>

      {/* Pay dialogs */}
      <PayInvoiceDialog
        open={payOpen}
        invoice={invoice}
        onPaid={createAfterPaid}
        onClose={() => setPayOpen(false)}
        onCancel={() => setPayOpen(false)}
      />

      <PayInvoiceDialog
        open={renewOpen}
        invoice={renewInvoice}
        onPaid={renewAfterPaid}
        onClose={() => setRenewOpen(false)}
        onCancel={() => setRenewOpen(false)}
      />
    </Box>
  );
}
